import React from 'react';
import { Box, Typography } from '@mui/material';
import Chevron from './Chevron';
import { FONT, badgeSx } from '../theme/tokens';

/** The one status/class/tag mark in the app — a small square + Share Tech
 * Mono text in a hairline-bordered rectangle, never a filled Chip pill.
 * Color lives in the text and the square, never a background fill
 * (TERMINAL DISCIPLINE, unchanged since the app's first pass). `dim` mutes
 * it for "no data yet" states. `icon` swaps the default square for another
 * small glyph (a pin/star for Dashboard favorites) without a caller ever
 * hand-rolling `badgeSx` locally. `size="md"` is a larger reading size for
 * pages that need it (Dashboard) without touching every other call site. */
const StatusBadge: React.FC<{
  label: string;
  accent: string;
  dim?: boolean;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
  /** Caps the badge and ellipsises the label instead of letting it run past
   * its container — for labels that carry user text (a fitting's name) in a
   * narrow column. Unset keeps the default: never wrap, never truncate. */
  maxWidth?: number | string;
}> = ({ label, accent, dim, icon, size = 'sm', maxWidth }) => (
  <Box
    title={maxWidth ? label : undefined}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: size === 'md' ? 0.75 : 0.5,
      px: size === 'md' ? 1.25 : 1,
      py: size === 'md' ? 0.4 : 0.25,
      ...badgeSx(accent),
      opacity: dim ? 0.6 : 1,
      ...(maxWidth ? { maxWidth, minWidth: 0 } : {}),
    }}
  >
    {icon ?? <Chevron color={accent} size={size === 'md' ? 8 : 7} />}
    <Typography
      sx={{
        fontFamily: FONT.mono,
        fontSize: size === 'md' ? '0.82rem' : '0.7rem',
        color: accent,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        // minWidth:0 as well as overflow: the label is a flex item of the
        // badge, and min-width:auto would keep it at its full text width no
        // matter what the badge's own maxWidth says.
        ...(maxWidth ? { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
      }}
    >
      {label}
    </Typography>
  </Box>
);

export default StatusBadge;
